import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Avatar,
  Chip,
  Box,
  IconButton,
  makeStyles,
  Fade,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  InputAdornment,
  Divider,
  Alert,
  FormHelperText
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      width: '100%',
      maxWidth: 500,
      margin: theme.spacing(2),
      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
      background: theme.palette.background.paper,
      '@media (max-width: 600px)': {
        maxWidth: '95vw',
        maxHeight: '90vh',
        margin: theme.spacing(1),
        borderRadius: 12,
      }
    }
  },
  dialogTitle: {
    padding: '20px 24px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTypography-h6': {
      fontWeight: 600,
      fontSize: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    }
  },
  dialogContent: {
    padding: '24px',
    minHeight: 200,
  },
  dialogActions: {
    padding: '16px 24px 24px',
    gap: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  titleField: {
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  usersList: {
    maxHeight: 240,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.default,
    ...theme.scrollbarStyles,
  },
  userItem: {
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light + '20',
      borderLeft: `3px solid ${theme.palette.primary.main}`,
    }
  },
  userAvatar: {
    width: 40,
    height: 40,
    fontSize: '14px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  selectedUsers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: 12,
    minHeight: 56,
    alignItems: 'flex-start',
    maxHeight: 120,
    overflow: 'auto',
    ...theme.scrollbarStyles,
  },
  selectedChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '& .MuiChip-deleteIcon': {
      color: theme.palette.primary.contrastText,
    }
  },
  searchField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: theme.palette.background.default,
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  primaryButton: {
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
    '&:hover': {
      boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
    },
  },  secondaryButton: {
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
  },
  chatTypeIndicator: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
  },
  chatTypeIcon: {
    padding: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  titleFieldDisabled: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.action.disabledBackground,
      '& fieldset': {
        borderColor: theme.palette.action.disabled,
      }
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.disabled,
    }
  },
  validationMessage: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorMessage: {
    backgroundColor: theme.palette.error.light + '20',
    color: theme.palette.error.dark,
    border: `1px solid ${theme.palette.error.light}`,
  },
  warningMessage: {
    backgroundColor: theme.palette.warning.light + '20',
    color: theme.palette.warning.dark,
    border: `1px solid ${theme.palette.warning.light}`,
  },
  successMessage: {
    backgroundColor: theme.palette.success.light + '20',
    color: theme.palette.success.dark,
    border: `1px solid ${theme.palette.success.light}`,
  },
}));

const ModernChatModal = ({
  open,
  onClose,
  onSave,
  title: modalTitle = '',
  type = 'new', // 'new' ou 'edit'
  chat = null,
  loading = false
}) => {
  const classes = useStyles();
  const { user: currentUser } = useContext(AuthContext);
  
  const [title, setTitle] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [privateExists, setPrivateExists] = useState(false);
  const [existingChatInfo, setExistingChatInfo] = useState(null);
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  // ✅ Determinar tipo de chat automaticamente
  const chatType = useMemo(() => {
    const totalParticipants = selectedUsers.length + 1; // +1 para o usuário atual
    // ✅ Sempre começa como privado, muda para grupo se mais de 2 participantes
    return totalParticipants > 2 ? 'group' : 'private';
  }, [selectedUsers.length]);

  // ✅ Determinar nome do chat automaticamente
  const autoGeneratedTitle = useMemo(() => {
    if (chatType === 'private' && selectedUsers.length === 1) {
      return selectedUsers[0].name;
    }
    return title;
  }, [chatType, selectedUsers, title]);

  // ✅ Campo título deve ser bloqueado para chats privados
  const isTitleDisabled = chatType === 'private';

  // ✅ Validação em tempo real
  useEffect(() => {
    const validateForm = async () => {
      const errors = [];
      
      // Validar número de participantes
      if (selectedUsers.length === 0) {
        errors.push({
          type: 'error',
          message: 'Selecione pelo menos um participante'
        });
      }

      // Validar título para grupos
      if (chatType === 'group') {
        if (!title.trim()) {
          errors.push({
            type: 'error',
            message: 'Nome do grupo é obrigatório'
          });
        } else if (title.trim().length < 3) {
          errors.push({
            type: 'error',
            message: 'Nome do grupo deve ter pelo menos 3 caracteres'
          });
        } else {
          // Verificar se nome do grupo já existe (só para grupos novos)
          if (type === 'new') {
            setNameCheckLoading(true);
            try {
              const { data } = await api.get('/chats/check-name', {
                params: { name: title.trim() }
              });
              if (data.exists) {
                errors.push({
                  type: 'error',
                  message: 'Já existe um grupo com este nome'
                });
              }
            } catch (err) {
              console.error('Erro ao verificar nome do grupo:', err);
            } finally {
              setNameCheckLoading(false);
            }
          }
        }
      }

      // Verificar se já existe chat privado entre os usuários (só para chats privados novos)
      if (chatType === 'private' && selectedUsers.length === 1 && type === 'new') {
        try {
          const { data } = await api.get('/chats/check-private', {
            params: { userId: selectedUsers[0].id }
          });
          setPrivateExists(!!data.exists);
          setExistingChatInfo(data.exists ? { chatId: data.chatId, chatUuid: data.chatUuid } : null);
        } catch (err) {
          console.error('Erro ao verificar chat privado:', err);
          setPrivateExists(false);
          setExistingChatInfo(null);
        }
      } else {
        setPrivateExists(false);
        setExistingChatInfo(null);
      }

      setValidationErrors(errors);
    };

    // Debounce para evitar muitas chamadas da API
    const timeoutId = setTimeout(validateForm, 500);
    return () => clearTimeout(timeoutId);
  }, [title, selectedUsers, chatType, type]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (open) {
      setTitle(type === 'edit' && chat ? chat.title : '');
      setSelectedUsers(type === 'edit' && chat ? chat.users?.map(u => ({ id: u.user.id, name: u.user.name })) || [] : []);
      setSearchTerm('');
      setValidationErrors([]);
      setPrivateExists(false);
      setExistingChatInfo(null);
      loadUsers();
    } else {
      setTitle('');
      setSelectedUsers([]);
      setSearchTerm('');
      setAllUsers([]);
      setValidationErrors([]);
      setPrivateExists(false);
      setExistingChatInfo(null);
    }
  }, [open, type, chat]);
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get('/users/list');
      const userList = data
        .filter(user => user.id !== currentUser?.id) // ✅ Filtrar usuário atual
        .map(user => ({
          id: user.id,
          name: user.name,
          email: user.email || ''
        }));
      setAllUsers(userList);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      toastError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserToggle = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        title: autoGeneratedTitle.trim(), // ✅ Usar título auto-gerado
        users: selectedUsers,
        type: chatType // ✅ Incluir tipo do chat
      });
      onClose();
    } catch (err) {
      console.error('Erro ao salvar chat:', err);
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    // ✅ Validação híbrida
    const hasUsers = selectedUsers.length > 0;
    const hasValidTitle = chatType === 'private' || (chatType === 'group' && title.trim().length >= 3);
    const hasNoErrors = validationErrors.filter(err => err.type === 'error').length === 0;
    
    return hasUsers && hasValidTitle && hasNoErrors && !nameCheckLoading;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const filteredUsers = allUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      TransitionComponent={Fade}
      transitionDuration={300}
    >      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6">
          {chatType === 'private' ? <PersonIcon /> : <GroupIcon />}
          {type === 'edit' ? 'Editar Conversa' : 'Nova Conversa'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>      <DialogContent className={classes.dialogContent}>
        {/* ✅ SEÇÃO 1: SELEÇÃO DE PARTICIPANTES */}
        <Typography className={classes.sectionTitle}>
          Participantes
        </Typography>

        {/* Campo de busca */}
        <TextField
          fullWidth
          placeholder="Buscar usuários..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Lista de usuários */}
        <div className={classes.usersList}>
          {loadingUsers ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={24} />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">
                {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário disponível'}
              </Typography>
            </div>
          ) : (
            <List dense>
              {filteredUsers.map((user) => {
                const isSelected = selectedUsers.some(u => u.id === user.id);
                return (
                  <ListItem
                    key={user.id}
                    className={`${classes.userItem} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleUserToggle(user)}
                  >
                    <ListItemAvatar>
                      <Avatar className={classes.userAvatar}>
                        {getInitials(user.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.name}
                      secondary={user.email}
                    />
                    <Checkbox
                      checked={isSelected}
                      color="primary"
                      size="small"
                    />
                  </ListItem>
                );
              })}
            </List>
          )}        </div>

        {/* ✅ SEÇÃO 2: INFORMAÇÕES DO CHAT (sempre visível) */}
        <Divider style={{ margin: '24px 0 16px' }} />
        
        {/* Indicador do tipo de chat */}
        <div className={classes.chatTypeIndicator}>
          <div className={classes.chatTypeIcon}>
            {chatType === 'private' ? <PersonIcon fontSize="small" /> : <GroupIcon fontSize="small" />}
          </div>
          <div>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {chatType === 'private' ? 'Conversa Privada' : 'Grupo'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {selectedUsers.length === 0 
                ? 'Selecione participantes para continuar'
                : chatType === 'private' 
                  ? `Conversa individual com ${selectedUsers[0]?.name}`
                  : `Grupo com ${selectedUsers.length + 1} participantes`
              }
            </Typography>
          </div>
        </div>

        {/* Campo de título dinâmico */}
        <TextField
          fullWidth
          label={chatType === 'private' ? 'Nome da conversa (automático)' : 'Nome do grupo'}
          placeholder={
            selectedUsers.length === 0 
              ? 'Primeiro selecione os participantes...'
              : chatType === 'private' 
                ? 'Será definido automaticamente...' 
                : 'Digite um nome para o grupo...'
          }
          value={chatType === 'private' ? autoGeneratedTitle : title}
          onChange={(e) => setTitle(e.target.value)}
          variant="outlined"
          className={`${classes.titleField} ${isTitleDisabled ? classes.titleFieldDisabled : ''}`}
          disabled={isTitleDisabled || selectedUsers.length === 0}
          helperText={
            selectedUsers.length === 0
              ? 'Selecione pelo menos um participante'
              : chatType === 'private' 
                ? 'Nome definido automaticamente com base no participante'
                : `${title.length}/50 caracteres (mínimo 3)`
          }
          error={chatType === 'group' && title.length > 0 && title.length < 3}
          inputProps={{ maxLength: 50 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {chatType === 'private' ? <PersonIcon color="action" /> : <GroupIcon color="action" />}
              </InputAdornment>
            ),
            endAdornment: nameCheckLoading && (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ),
          }}
        />

        {/* ✅ Aviso específico para chat privado existente */}
        {privateExists && selectedUsers.length === 1 && chatType === 'private' && (
          <div style={{ marginTop: 16 }}>
            <div className={`${classes.validationMessage} ${classes.warningMessage}`}>
              <WarningIcon fontSize="small" />
              {`Já existe uma conversa privada com ${selectedUsers[0].name}`}
            </div>
          </div>
        )}

        {/* ✅ Mensagens de validação (sempre visível se há erros) */}
        {validationErrors.length > 0 && (
          <div style={{ marginTop: 16 }}>
            {validationErrors.map((error, index) => (
              <div 
                key={index} 
                className={`${classes.validationMessage} ${
                  error.type === 'error' ? classes.errorMessage :
                  error.type === 'warning' ? classes.warningMessage :
                  classes.successMessage
                }`}
              >
                <WarningIcon fontSize="small" />
                {error.message}
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          className={classes.secondaryButton}
          disabled={saving}
        >
          Cancelar
        </Button>        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          className={classes.primaryButton}
          disabled={!isFormValid() || saving || (chatType === 'private' && privateExists)}
          startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
        >
          {saving ? 'Criando...' : (
            type === 'edit' ? 'Salvar Alterações' : 
            chatType === 'private' ? 'Iniciar Conversa' : 'Criar Grupo'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModernChatModal;
